import React, { createRef, useEffect, useState } from 'react';
import { Grid, MenuItem, Paper, useMediaQuery, useTheme } from '@material-ui/core';
import { Chip, ChipProps, LoadingBounce, Select, TextField } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { ModalSectionTitle } from 'components/ClosableDialog/ModalSectionTitle';
import { GetPartId } from 'compositions/OrderDetail/components/PartsAndServices/GetPartId';
import WizardTireInformation from 'compositions/Wizard/SelectTiresAndParts/WizardTireInformation';
import { useGetAdditionalServices } from 'hooks/useGetAdditionalServices';
import { useGetPrices } from 'hooks/useGetPrices';
import { useGetTires } from 'hooks/useGetTires';
import {
  AdditionalServicesType,
  CountriesType,
  LocationCore,
  PartPrices,
  PqlPriceType,
  UFOPart,
  VehicleType,
} from 'types/UFOTypes';
import { rgbToRgba } from 'utils/format';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { BREAKPOINT } from 'utils/responsivenessUtils';
import { SelectedServicesType, SelectedTire } from '../WizardContext';
import { WizardAdditionalServices } from './WizardAdditionalServices';

const getFoundPartsChipColor = (loading: boolean, itemsFound: number | null): ChipProps['color'] => {
  if (loading) return 'secondary';

  if (itemsFound === null) return 'danger';

  return 'success';
};

export const CustomParts = (props: {
  setSelectedTires: React.Dispatch<React.SetStateAction<SelectedTire[]>>;
  selectedTires: SelectedTire[];
  fittingTires: string[];
  provider: LocationCore | null;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  fleet_id: string;
  commonPricesParams: CommonPricesParamsType;
  setSelectedServices: React.Dispatch<React.SetStateAction<SelectedServicesType[]>>;
  selectedServices: SelectedServicesType[];
  searchFor?: 'mspn' | 'size' | 'services';
  vehicle?: VehicleType;
  searchedPart?: string;
  setPartsList: React.Dispatch<React.SetStateAction<UFOPart[]>>;
  isMobile: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down(BREAKPOINT));

  const {
    setSelectedTires,
    selectedTires,
    provider,
    fleet_id: fleetId,
    commonPricesParams,
    setSelectedServices,
    selectedServices,
    vehicle,
    searchedPart,
    setPartsList,
    fittingTires,
    searchFor: searchForProp,
    isMobile,
  } = props;
  const [query, setQuery] = useState<string>();
  const [tireSizeId, setTireSizeId] = useState<number>();
  const [mspn, setMspn] = useState<string>();
  const [searchFor, setSearchFor] = useState<'mspn' | 'size' | 'services'>(searchForProp ?? 'size');

  const [highlight, setHighlight] = useState(false);
  const [refs, setRefs] = useState<any>();

  const options: { label: string; value: number }[] = [];

  const tiresResponse = useGetTires(
    (tireSizeId || (mspn && mspn.length > 3)) && (searchFor === 'size' || searchFor === 'mspn')
      ? {
          country_code: (provider?.customer_country?.toLocaleLowerCase() as CountriesType) || 'us',
          custom_tire: true,
          with_service_mspn: true,
          lump: true,
          tire_size_id: tireSizeId,
          mspn,
        }
      : null,
  );

  const additionalServicesResp = useGetAdditionalServices(
    query && searchFor === 'services'
      ? {
          with_service_mspn: true,
          fleet_id: fleetId,
          dealer_id: provider?.customer_number || '',
          isMobile,
          query,
        }
      : null,
  );

  const servicesPricesResponse = useGetPrices(
    !additionalServicesResp.isLoading && !!additionalServicesResp.data?.addlServices?.length
      ? {
          ...commonPricesParams,
          parts: additionalServicesResp.data.addlServices.map((s) => ({
            mspn: getMspn(s) as string,
            qty: 1,
            is_pql: s.ispql || false,
          })),
        }
      : null,
  );
  const tiresPricesResponse = useGetPrices(
    !tiresResponse.isLoading && !!tiresResponse.data?.length
      ? {
          ...commonPricesParams,

          parts: tiresResponse.data.map((t) => ({ mspn: t.mspn, qty: 1, is_pql: false })),
        }
      : null,
  );

  let pricingErrorMspn: string[] = [];
  const checkPricingError = (
    parts: UFOPart[] | AdditionalServicesType[],
    partsPrices: PartPrices[],
    pqlPartsPrices?: PqlPriceType[],
  ) => {
    partsPrices.forEach((p) => {
      if (p.error && !pricingErrorMspn.includes(getMspn(p) as string))
        pricingErrorMspn = [...pricingErrorMspn, getMspn(p) as string];
    });
    parts.forEach((p) => {
      if (
        !partsPrices.some((pr) => getMspn(pr) === getMspn(p)) &&
        !pqlPartsPrices?.some((pr) => getMspn(pr) === getMspn(p))
      ) {
        pricingErrorMspn = [...pricingErrorMspn, getMspn(p) as string];
      }
    });
  };
  if (tiresPricesResponse.data?.parts && tiresResponse.data) {
    checkPricingError(tiresResponse.data, tiresPricesResponse.data.parts);
  }
  if (servicesPricesResponse.data && additionalServicesResp.data) {
    checkPricingError(
      additionalServicesResp.data?.addlServices,
      servicesPricesResponse.data.parts,
      servicesPricesResponse.data.pqlParts,
    );
  }

  const loading =
    tiresResponse.isLoading ||
    additionalServicesResp.isLoading ||
    servicesPricesResponse.isLoading ||
    tiresPricesResponse.isLoading;
  const filteredParts = tiresResponse.data?.filter((p) => !pricingErrorMspn.includes(getMspn(p) as string));
  const filteredAdditionalServices = additionalServicesResp.data?.addlServices.filter(
    (p) => !pricingErrorMspn.includes(getMspn(p) as string),
  );

  const scrollToPosition = (id) => {
    setTimeout(() => {
      if (!refs || !refs[id].current) return;
      refs[id].current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }, 500);
  };

  let itemsFound: number | null = null;
  if ((searchFor === 'size' || searchFor === 'mspn') && !loading && tiresResponse.data)
    itemsFound = filteredParts?.length ?? null;
  if (searchFor === 'services' && query && !loading && additionalServicesResp.data)
    itemsFound = filteredAdditionalServices?.length ?? null;

  const noFoundRef = createRef<HTMLDivElement>();
  const tireListRef = createRef<HTMLUListElement>();

  useEffect(() => {
    if (!loading && filteredParts && filteredParts.length > 0) {
      const locRefs = [...filteredParts].reduce((acc, value) => {
        acc[value.mspn] = createRef();
        return acc;
      }, {});
      setRefs(locRefs);
      setPartsList(filteredParts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiresResponse.data]);

  useEffect(() => {
    if (searchedPart) {
      let cont = 0;
      scrollToPosition(searchedPart);
      const interval = setInterval(() => {
        cont += 1;
        setHighlight((prev) => !prev);
        if (cont > 16) {
          clearInterval(interval);
          setHighlight(false);
        }
      }, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedPart]);

  useEffect(() => {
    if (!loading && filteredParts && searchFor === 'size' && itemsFound) {
      tireListRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    if (!loading && filteredParts && searchFor === 'size' && !itemsFound) {
      noFoundRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredParts]);

  for (let i = 1; i <= 8; i += 1) {
    options.push({ label: t('Qty: {{i}}', { i }), value: i });
  }

  const searchOptions = [
    { label: 'Search by Tire Size', value: 'size' },
    { label: 'Search Tires by Part Number', value: 'mspn' },
    { label: 'Services', value: 'services' },
  ];

  const FoundPartsChip = React.useCallback(() => {
    const color = getFoundPartsChipColor(loading, itemsFound);

    return (
      <Chip style={{ zoom: 0.8 }} color={color} label={loading ? <LoadingBounce /> : t('{{qty}} found', {qty: itemsFound ?? 0})} />
    );
  }, [t, loading, itemsFound]);

  if (!provider || !fleetId) return null;
  let title = '';
  let subtitle = '';
  switch (searchForProp) {
    case 'size':
      title = 'Search tires by size';
      subtitle = 'Select the size of the tire you want to add to your order';
      break;
    case 'mspn':
      title = 'Search by part number';
      subtitle = vehicle?.id
        ? 'Search for custom Parts & Services by Part Number'
        : 'Search for custom Parts by Part Number';
      break;
    case 'services':
      title = 'Search for services';
      subtitle = 'Search the services you want to add to your order';
      break;
    default:
      title = 'Custom Parts & Services';
      subtitle = 'Search for custom Parts & Services';
  }

  return (
    <div
      style={
        mobileView
          ? {
              overflowY: 'scroll',
              overflowX: 'hidden',
            }
          : undefined
      }
    >
      <ModalSectionTitle title={t(title)} subtitle={t(subtitle)} />
      <Grid container spacing={2}>
        {!searchForProp && (
          <Grid item xs={12} md={3}>
            <div style={{ margin: mobileView ? '0px 8px' : undefined }}>
              <Select
                id="lookup-selector"
                fullWidth
                value={searchFor}
                onChange={(event) => {
                  setSearchFor(event.target.value as 'size' | 'services' | 'mspn');
                  if (event.target.value === 'size') setMspn(undefined);
                  if (event.target.value === 'mspn') setTireSizeId(undefined);
                }}
                margin="dense"
                label={t('Look for')}
              >
               {searchOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {t(o.label)}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </Grid>
        )}
        <Grid item xs={12}>
          {searchFor === 'services' && (
            <div style={{ display: 'flex', alignItems: 'end', margin: mobileView ? '0px 8px' : undefined }}>
              <TextField
                style={{ marginTop: 21 }}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('Search by MSPN or service name')}
                value={query}
                id='custom-parts-search-field'
                name='custom-parts-search-field'
              />
              <FoundPartsChip />
            </div>
          )}
          {searchFor === 'size' && (
            <GetPartId
              onGetSizeId={(s) => {
                if (s) setTireSizeId(s);
              }}
            />
          )}
          {searchFor === 'mspn' && (
            <div style={{ display: 'flex', alignItems: 'end', margin: mobileView ? '0px 8px' : undefined }}>
              <TextField
                style={{ marginTop: 21 }}
                onChange={(e) => setMspn(e.target.value)}
                placeholder={t('Search by Part Number')}
                id='custom-parts-search-field'
                name='custom-parts-search-field'
                value={mspn}
              />
              <FoundPartsChip />
            </div>
          )}
        </Grid>
      </Grid>
      <ul ref={tireListRef} style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
        <Grid container spacing={0} style={{ margin: 0, padding: 0 }}>
          {!loading &&
            !!filteredParts?.length &&
            (searchFor === 'size' || searchFor === 'mspn') &&
            filteredParts?.map((tire) => (
              <Grid item xs={12} md={6} style={{ margin: 0, padding: 0 }}>
                <li key={tire.mspn} ref={refs && tire.mspn in refs ? refs[tire.mspn] : undefined}>
                  <Paper
                    elevation={2}
                    style={{
                      padding: 16,
                      margin: 8,
                      background: searchedPart === getMspn(tire) && highlight ? 'rgba(177, 233, 233, 0.5)' : undefined,
                    }}
                    id="partPaper"
                  >
                    <WizardTireInformation
                      isLumped={!!tire?.child?.length}
                      tire={tire}
                      vehicle={vehicle || undefined}
                      selectedTires={selectedTires}
                      setSelectedTires={setSelectedTires}
                      commonPricesParams={commonPricesParams}
                      fittingTires={fittingTires}
                    />
                  </Paper>
                </li>
              </Grid>
            ))}
        </Grid>
      </ul>
      {!loading && searchFor === 'services' && !!filteredAdditionalServices?.length && (
        <WizardAdditionalServices
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          additionalServices={filteredAdditionalServices}
          commonPricesParams={commonPricesParams}
        />
      )}
      {loading && !mobileView && <LoadingBounce />}
      <div ref={noFoundRef} style={{ minHeight: 100 }}>
        {itemsFound === 0 && <ModalSectionTitle title={t('Part not found')} />}
      </div>
      {loading && mobileView && searchFor === 'size' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: rgbToRgba(theme.palette.background.default, 0.2),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LoadingBounce />
        </div>
      )}
    </div>
  );
};
